import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, map, tap } from 'rxjs/operators';
import { messagesLoaded } from '../../conversations/messages/actions/message.actions';
import { GenesysCloudWorkItemNotificationsService } from 'shared/services/genesys-workitem-notifications/genesys-cloud-workitem-notifications.service';
import { selectMessageConversationById } from 'common/store/genesys-cloud/conversations/messages/selectors/message-conversation.selectors';
import { selectContactById } from 'common/store/genesys-cloud/external-contacts/selectors/external-contact.selectors';
import { messageConversationChanged } from 'common/store/genesys-cloud/conversations/messages/actions/message-conversation.actions';
import { MessageData, MessageDirection } from 'common/store/genesys-cloud/conversations/messages/models/message-data.model';
import { ParticipantPurpose } from 'common/store/genesys-cloud/conversations/common/models/participant-purpose.enum';
import { Participant } from 'common/store/genesys-cloud/conversations/common/models/participant.model';
import { ConnectionState } from 'common/store/genesys-cloud/conversations/common/models/connection-state.enum';
import { minBy } from 'lodash';
import { emailConversationChanged } from 'common/store/genesys-cloud/conversations/emails/actions/email-conversation.actions';

@Injectable()
export class GenesysCloudDesktopNotificationEffects {

  readonly newConversationInQueue$ = createEffect(() => this.actions$.pipe(
    ofType(messageConversationChanged),
    map(({ conversation }) => conversation),
    filter(conversation => conversation.isAcceptable),
    filter(conversation => this.hasConnectedParticipantMessage(
      conversation.participants.find( participant => participant.purpose === ParticipantPurpose.ACD))
    ),
    tap((conversation) => this.workItemNotificationsService.notifyNewConversation(conversation.id, conversation.startTime))
  ), { dispatch: false });

  readonly notifyNewEmailArrived$ = createEffect(() => this.actions$.pipe(
    ofType(emailConversationChanged),
    map(({ conversation }) => conversation),
    filter(conversation => conversation.isAcceptable),
    tap((conversation) => this.workItemNotificationsService.notifyNewEmailConversation(conversation.id, conversation.startTime))
  ), { dispatch: false });

  readonly newMessageInConversation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(messagesLoaded),
      filter(({ messages }) => this.hasInboundMessages(messages)),
      map(({ conversationId, messages }) => ({ conversationId: conversationId, message: this.firstInboundMessage(messages) })),
      concatLatestFrom(({ conversationId }) => this.store.select(selectMessageConversationById(conversationId))),
      map(([{ message }, conversation]) => ({ message, conversation })),
      concatLatestFrom(({ conversation }) => this.store.select(selectContactById(conversation.customerParticipant.externalContactId))),
      map(([{ message, conversation }, contact]) => ({ message, conversation, contact })),
      tap(({ message, conversation, contact }) => {
        this.workItemNotificationsService.notifyNewMessage(contact?.firstName, message?.textBody, conversation.id, message.timestamp);
      })
    );
  }, { dispatch: false });

  private hasInboundMessages(messages: MessageData[]): boolean {
    return messages.some(message => message.direction === MessageDirection.Inbound);
  }

  private hasConnectedParticipantMessage(participant: Participant): boolean {
    return participant.messages?.some(pm => pm.state.toLowerCase() === ConnectionState.Connected);
  }

  private firstInboundMessage(messages: MessageData[]): MessageData {
    const inboundMessages =  messages.filter((message) => message.direction === MessageDirection.Inbound);
    return minBy(inboundMessages, m => m.timestamp);
  }

  constructor(private readonly actions$: Actions,
              private readonly store: Store,
              private readonly workItemNotificationsService: GenesysCloudWorkItemNotificationsService) {}
}
